import { useState, type FunctionComponent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, DatePicker, Drawer, Form, Input, InputNumber, Radio, Select, Space, Switch, message } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  ChallengeGoalType,
  ChallengeLeaderboardVisibility,
  ChallengeStatus,
  DEFAULT_CHALLENGE_COVER_POSITION,
} from "@fitness-app/data-models/entities/Challenge";
import { ChallengeFeedMode } from "@fitness-app/data-models/entities/ChallengeFeed";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { supabase } from "~/store/initializeStore";
import ChallengeCoverField from "./ChallengeCoverField";
import { CHALLENGE_FEED_MODE_TOOLTIP, challengeFeedModeOptions } from "./constants";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

type ChallengeFormValues = {
  name: string;
  description?: string;
  dateRange: [Dayjs, Dayjs];
  selfJoinEnabled: boolean;
  joinMode: "open" | "code";
  leaderboardVisibility: ChallengeLeaderboardVisibility;
  goalType: ChallengeGoalType;
  passPoints?: number;
  feedMode: ChallengeFeedMode;
};

const goalTypeNeedsPassPoints = (goalType: ChallengeGoalType | undefined) =>
  goalType === ChallengeGoalType.Threshold || goalType === ChallengeGoalType.Both;

/** Short, unambiguous join code (no 0/O/1/I) — unique index on challenge.joinCode guards collisions. */
export const generateJoinCode = (): string => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 6 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join("");
};

export const leaderboardVisibilityOptions = [
  { value: ChallengeLeaderboardVisibility.Full, label: "Pełny ranking" },
  { value: ChallengeLeaderboardVisibility.Top5PlusYou, label: "Top 5 + Twoje miejsce" },
  { value: ChallengeLeaderboardVisibility.Anonymized, label: "Twoje miejsce, reszta anonimowa" },
];

export const challengeGoalTypeOptions = [
  { value: ChallengeGoalType.Leaderboard, label: "Ranking (rywalizacja)" },
  { value: ChallengeGoalType.Threshold, label: "Próg punktowy (każda może zaliczyć)" },
  { value: ChallengeGoalType.Both, label: "Próg + ranking" },
];

// Same lifecycle rules as recompute-active-challenges-task. Saved as `upcoming` while its window
// already contains today, a challenge keeps its tasks locked in the apps until the next sweep.
const getInitialChallengeStatus = (start: Dayjs, end: Dayjs): ChallengeStatus => {
  const today = dayjs();
  if (end.isBefore(today, "day")) {
    return ChallengeStatus.Finished;
  }
  if (start.isAfter(today, "day")) {
    return ChallengeStatus.Upcoming;
  }
  return ChallengeStatus.Active;
};

export const ChallengeFormDrawer: FunctionComponent<Props> = ({ open, onClose, onCreated }) => {
  const [form] = Form.useForm<ChallengeFormValues>();
  const { memberId } = useUserClaims();
  // TRE-1583 — cover image (upload or AI); kept outside the Form (it's set by side-effect buttons).
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverPositionX, setCoverPositionX] = useState(DEFAULT_CHALLENGE_COVER_POSITION);
  const [coverPositionY, setCoverPositionY] = useState(DEFAULT_CHALLENGE_COVER_POSITION);
  const watchedName = Form.useWatch("name", form);
  const watchedDescription = Form.useWatch("description", form);

  const createChallengeMutation = useMutation({
    mutationFn: async (values: ChallengeFormValues) => {
      const [start, end] = values.dateRange;
      const joinCode = values.selfJoinEnabled && values.joinMode === "code" ? generateJoinCode() : null;
      const { error } = await supabase.from("challenge").insert({
        name: values.name,
        description: values.description ?? null,
        startDate: start.startOf("day").toISOString(),
        endDate: end.endOf("day").toISOString(),
        status: getInitialChallengeStatus(start, end),
        selfJoinEnabled: values.selfJoinEnabled,
        joinCode,
        leaderboardVisibility: values.leaderboardVisibility,
        goalType: values.goalType,
        passPoints: goalTypeNeedsPassPoints(values.goalType) ? values.passPoints ?? null : null,
        feedMode: values.feedMode,
        coverImage,
        coverPositionX,
        coverPositionY,
        createdBy: memberId ?? null,
      } as never);

      if (error) {
        throw new Error(error.message);
      }

      return joinCode;
    },
    onSuccess: (joinCode) => {
      void message.success(joinCode ? `Wyzwanie utworzone — kod dołączenia: ${joinCode}` : "Wyzwanie utworzone");
      form.resetFields();
      setCoverImage(null);
      setCoverPositionX(DEFAULT_CHALLENGE_COVER_POSITION);
      setCoverPositionY(DEFAULT_CHALLENGE_COVER_POSITION);
      onCreated();
    },
    onError: (error: Error) => {
      void message.error(`Nie udało się utworzyć wyzwania: ${error.message}`);
    },
  });

  return (
    <Drawer
      title="Nowe wyzwanie"
      width={480}
      open={open}
      onClose={() => {
        // Cancel must not leak a draft cover into the next "new challenge" session.
        setCoverImage(null);
        setCoverPositionX(DEFAULT_CHALLENGE_COVER_POSITION);
        setCoverPositionY(DEFAULT_CHALLENGE_COVER_POSITION);
        onClose();
      }}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Anuluj</Button>
          <Button type="primary" loading={createChallengeMutation.isLoading} onClick={() => form.submit()}>
            Utwórz
          </Button>
        </Space>
      }
    >
      <Form<ChallengeFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          selfJoinEnabled: false,
          joinMode: "open",
          leaderboardVisibility: ChallengeLeaderboardVisibility.Full,
          goalType: ChallengeGoalType.Leaderboard,
          feedMode: ChallengeFeedMode.None,
          dateRange: [dayjs(), dayjs().add(30, "day")],
        }}
        onFinish={(values) => createChallengeMutation.mutate(values)}
      >
        <Form.Item
          name="name"
          label="Nazwa"
          rules={[{ required: true, message: "Podaj nazwę wyzwania" }]}
        >
          <Input placeholder="np. Czerwcowe Wyzwanie 🔥" />
        </Form.Item>
        <Form.Item name="description" label="Opis">
          <Input.TextArea rows={3} placeholder="Krótki opis wyzwania" />
        </Form.Item>
        <Form.Item
          name="dateRange"
          label="Okres trwania"
          rules={[{ required: true, message: "Wybierz okres trwania" }]}
        >
          <DatePicker.RangePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>
        <Form.Item label="Okładka" tooltip="Widoczna na karcie wyzwania i w nagłówku w aplikacji klientki">
          <ChallengeCoverField
            challengeId="new"
            challengeContext={{ name: watchedName ?? "", description: watchedDescription ?? null }}
            value={coverImage}
            onChange={setCoverImage}
            positionX={coverPositionX}
            positionY={coverPositionY}
            onPositionChange={(x, y) => {
              setCoverPositionX(x);
              setCoverPositionY(y);
            }}
          />
        </Form.Item>
        <Form.Item
          name="selfJoinEnabled"
          label="Samodzielne dołączanie"
          valuePropName="checked"
          tooltip="Pozwala klientkom dołączyć samodzielnie z aplikacji"
        >
          <Switch />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.selfJoinEnabled !== next.selfJoinEnabled}>
          {({ getFieldValue }) =>
            getFieldValue("selfJoinEnabled") ? (
              <Form.Item
                name="joinMode"
                label="Sposób dołączania"
                tooltip={
                  "Otwarte — wyzwanie widoczne na liście „Dostępne wyzwania\" w aplikacji (w ramach grupy " +
                  "docelowej), dołączenie jednym tapnięciem. Z kodem — ukryte z listy, dołączenie tylko po " +
                  "wpisaniu wygenerowanego kodu."
                }
              >
                <Radio.Group
                  options={[
                    { value: "open", label: "Otwarte (bez kodu)" },
                    { value: "code", label: "Tylko z kodem" },
                  ]}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item
          name="goalType"
          label="Cel wyzwania"
          tooltip={
            "Ranking — klasyczna rywalizacja o miejsca. Próg punktowy — każda uczestniczka, która zdobędzie " +
            "wymaganą liczbę punktów, zalicza wyzwanie (bezpieczny format bez rywalizacji). Próg + ranking — " +
            "obydwa naraz."
          }
        >
          <Select options={challengeGoalTypeOptions} />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, next) => prev.goalType !== next.goalType}>
          {({ getFieldValue }) =>
            goalTypeNeedsPassPoints(getFieldValue("goalType")) ? (
              <Form.Item
                name="passPoints"
                label="Próg zaliczenia (punkty)"
                rules={[{ required: true, message: "Podaj próg punktowy zaliczenia" }]}
                tooltip="Uczestniczka, która zdobędzie co najmniej tyle punktów, zalicza wyzwanie (na stałe)."
              >
                <InputNumber min={1} style={{ width: "100%" }} placeholder="np. 500" />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item
          name="leaderboardVisibility"
          label="Widoczność rankingu"
          tooltip={
            "Pełny — każda uczestniczka widzi cały ranking. Top 5 — widzi tylko czołówkę i swoje miejsce. " +
            "Anonimowy — widzi pozycje i punkty, ale nazwiska innych są ukryte. Trenerzy zawsze widzą całość."
          }
        >
          <Select options={leaderboardVisibilityOptions} />
        </Form.Item>
        <Form.Item name="feedMode" label="Tablica wyzwania" tooltip={CHALLENGE_FEED_MODE_TOOLTIP}>
          <Select options={challengeFeedModeOptions} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ChallengeFormDrawer;
